import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { normalizeV2FlowEdge } from './studioV2FlowModel';

/**
 * List/edit flow edges as a table (pairs with JSON tab).
 */
export default function StudioV2FlowEdgesTable({ text, setText }) {
  const [parseError, setParseError] = useState(null);

  const { def, nodeIds } = useMemo(() => {
    try {
      const d = JSON.parse(text || '{}');
      if (!d || typeof d !== 'object') throw new Error('Invalid');
      const ids = new Set((Array.isArray(d.nodes) ? d.nodes : []).map((n) => String(n.id)));
      setParseError(null);
      return { def: d, nodeIds: ids };
    } catch (e) {
      setParseError(e.message || 'Invalid JSON');
      return { def: null, nodeIds: new Set() };
    }
  }, [text]);

  const edges = def && Array.isArray(def.edges) ? def.edges : [];

  const commitEdges = (nextEdges) => {
    if (!def) return;
    const out = { ...def, edges: nextEdges.map((e) => normalizeV2FlowEdge(e)) };
    setText(JSON.stringify(out, null, 2));
  };

  const updateRow = (idx, patch) => {
    const next = edges.map((e, i) => (i === idx ? { ...e, ...patch } : e));
    commitEdges(next);
  };

  const addRow = () => {
    const nodesArr = Array.isArray(def?.nodes) ? def.nodes : [];
    const a = nodesArr[0]?.id != null ? String(nodesArr[0].id) : 'start';
    const b = nodesArr[1]?.id != null ? String(nodesArr[1].id) : 'end';
    const id = `e_${Date.now()}`;
    commitEdges([...edges, { id, source: a, target: b, kind: 'control' }]);
  };

  const removeRow = (idx) => {
    commitEdges(edges.filter((_, i) => i !== idx));
  };

  if (parseError) {
    return <Alert severity="error">Cannot edit edges: {parseError}</Alert>;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, overflow: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Edges</Typography>
          <Typography variant="caption" color="text.secondary">
            Control how nodes link. Use labels on branches from conditionals.
          </Typography>
        </Box>
        <Button size="small" variant="contained" startIcon={<AddOutlinedIcon />} onClick={addRow} sx={{ textTransform: 'none' }}>
          Add edge
        </Button>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Id</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Target</TableCell>
            <TableCell>Kind</TableCell>
            <TableCell>Label</TableCell>
            <TableCell align="right"> </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {edges.length === 0 && (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant="body2" color="text.secondary">No edges — add one or use the Graph tab.</Typography>
              </TableCell>
            </TableRow>
          )}
          {edges.map((e, idx) => (
            <TableRow key={e.id || idx}>
              <TableCell sx={{ maxWidth: 140 }}>
                <TextField
                  size="small"
                  fullWidth
                  value={e.id ?? ''}
                  onChange={(ev) => updateRow(idx, { id: ev.target.value })}
                />
              </TableCell>
              <TableCell sx={{ maxWidth: 140 }}>
                <TextField
                  size="small"
                  fullWidth
                  value={e.source ?? ''}
                  error={e.source && !nodeIds.has(String(e.source))}
                  onChange={(ev) => updateRow(idx, { source: ev.target.value })}
                />
              </TableCell>
              <TableCell sx={{ maxWidth: 140 }}>
                <TextField
                  size="small"
                  fullWidth
                  value={e.target ?? ''}
                  error={e.target && !nodeIds.has(String(e.target))}
                  onChange={(ev) => updateRow(idx, { target: ev.target.value })}
                />
              </TableCell>
              <TableCell sx={{ maxWidth: 100 }}>
                <TextField
                  size="small"
                  fullWidth
                  value={e.kind ?? 'control'}
                  onChange={(ev) => updateRow(idx, { kind: ev.target.value })}
                />
              </TableCell>
              <TableCell sx={{ maxWidth: 120 }}>
                <TextField
                  size="small"
                  fullWidth
                  value={e.label ?? ''}
                  onChange={(ev) => updateRow(idx, { label: ev.target.value })}
                />
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" color="error" aria-label="Remove edge" onClick={() => removeRow(idx)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
